import React from 'react';
import {View} from 'react-native';
import {Card, Image} from '@rn-vui/base';
import {makeStyles} from '@rn-vui/themed';

import brandlogo from '../../../images/brand/header-logo.png';
import {isLightColor} from '../../../theme/colorHelpers';
import {formatMemberSinceDate} from '../../../utils/date';
import Text from '../../themed/Text';
import RewardsProgramImage from '../ProgramImage';

import Barcode from './Barcode';

const useStyles = makeStyles(theme => {
  return {
    wrapperStyle: {
      alignItems: 'center',
    },
    containerStyle: {
      margin: 0,
      borderRadius: 20,
      backgroundColor: theme?.colors?.legacy?.background?.general?.primary,
      borderWidth: 0,
    },
    barcodeContainerStyle: {
      backgroundColor: theme.colors.white,
      borderRadius: theme.size.s2,
      padding: theme.size.s2,
      alignSelf: 'stretch',
      alignContent: 'center',
      marginTop: 16,
    },
    memberName: {
      color:
        theme.colors?.legacy?.icon?.header?.secondary ||
        theme.colors?.legacy?.icon?.header?.primary,
      fontWeight: 'bold',
      alignSelf: 'flex-start',
      marginTop: 10,
    },
    staffPill: {
      alignSelf: 'flex-start',
      marginTop: 10,
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 999,
      // DIS-1239: invert the on-card color pair so the pill always contrasts
      // with the card surface. `icon.header.*` resolves to the brand's on-card
      // text color (same token memberName uses) — guaranteed legible per brand.
      backgroundColor:
        theme.colors?.legacy?.icon?.header?.secondary ||
        theme.colors?.legacy?.icon?.header?.primary ||
        '#111928',
    },
    staffPillText: {
      // Pill text = card bg color so the pill reads as an inverted chip.
      color: theme.colors?.legacy?.background?.general?.primary || theme.colors?.white,
      fontSize: 11,
      fontWeight: 'bold',
      letterSpacing: 1.2,
      lineHeight: 14,
    },
    memberSince: {
      color:
        theme.colors?.legacy?.icon?.header?.secondary ||
        theme.colors?.legacy?.icon?.header?.primary,
      fontSize: 12,
      alignSelf: 'flex-start',
      marginTop: 4,
    },
  };
});

type MemberCardProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  logoImg?: any;
  cardNumber: string;
  name: string;
  pending?: boolean;
  memberSince?: string;
  isStaff?: boolean;
  isLmgStaff?: boolean;
  children?: ({color}: {color: string}) => React.ReactNode;
};

export default function MemberCard({
  logoImg = brandlogo,
  cardNumber = 'Example 1234',
  name,
  pending,
  memberSince,
  isStaff,
  isLmgStaff,
  children,
}: MemberCardProps) {
  const styles = useStyles();
  return (
    <Card wrapperStyle={styles.wrapperStyle} containerStyle={styles.containerStyle}>
      {!!logoImg && (
        <Image
          containerStyle={{justifyContent: 'center', alignContent: 'center'}}
          source={logoImg}
          style={{
            width: 120,
            height: 32,
          }}
          resizeMode="contain"
        />
      )}

      <RewardsProgramImage
        style={{marginTop: 12, marginBottom: 16}}
        variant={isLightColor(styles.containerStyle.backgroundColor) ? 'onLight' : 'onDark'}
      />

      <View style={[styles.barcodeContainerStyle]}>
        <View style={{alignItems: 'center'}}>
          <Barcode value={cardNumber} height={42} />
        </View>

        <View style={{marginTop: 10, alignItems: 'center'}}>
          {pending ? (
            <Text variant="body2" style={{fontStyle: 'italic'}}>
              Pending...
            </Text>
          ) : (
            <Text>{cardNumber}</Text>
          )}
        </View>
      </View>
      {isLmgStaff ? (
        <View style={styles.staffPill}>
          <Text style={styles.staffPillText}>LMG STAFF</Text>
        </View>
      ) : (
        !!isStaff && (
          <View style={styles.staffPill}>
            <Text style={styles.staffPillText}>STAFF</Text>
          </View>
        )
      )}
      <Text style={[styles.memberName]}>{name}</Text>
      {!!memberSince && (
        <Text style={[styles.memberSince]}>
          {`Member since ${formatMemberSinceDate(memberSince)}`}
        </Text>
      )}
      {!!pending && (
        <View
          style={{
            position: 'absolute',
            backgroundColor: 'white',
            opacity: 0.5,
            left: -16,
            right: -16,
            top: -16,
            bottom: -16,
          }}
        />
      )}
      {typeof children === 'function' ? children({color: styles.memberName.color}) : children}
    </Card>
  );
}
